import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { VFlex } from '@/components/base';
import Input from '@/components/Inputs/Input';
import Modal, { useModalContext } from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { createReportMutationOptions } from '@/entities/report';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services';
import { useToast } from '@/stores/pure/ToastStore';
import { track } from '@/utils/track/track';

import { reportUtils } from '../../utils';

type Props = {
  dashboard_alias: string;
};

const CreateReportModal = (props: Props) => {
  const { closeModal } = useModalContext();
  const [isLoading, setIsLoading] = useState(false);
  const addToast = useToast();
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const wr = useWorkspaceRouter();
  const createReportMutation = useMutation(
    createReportMutationOptions(queryClient),
  );
  const {
    data: dashboard,
    isLoading: isDashboardLoading,
    error: dashboardError,
  } = useQuery({
    queryKey: ['dashboard', props.dashboard_alias],
    queryFn: () => service.dashboard2.fetchDashboard(props.dashboard_alias),
  });

  useEffect(() => {
    if (dashboard) {
      setTitle(dashboard.title);
    }
  }, [dashboard]);

  useEffect(() => {
    if (dashboardError) {
      console.error(dashboardError);
      throw new Error('Fail to get dashboard');
    }
  }, [dashboardError]);

  const [title, setTitle] = useState<string>('');

  const onConfirm = async () => {
    try {
      if (!dashboard) throw new Error('Dashboard not found');
      setIsLoading(true);
      const reportBody = reportUtils.buildReport(dashboard);
      const res = await createReportMutation.mutateAsync({
        workspace_id: workspaceId,
        ...reportBody,
        title,
      });

      // Track report create complete event
      track(AMP_KEYS.REPORT_CREATE_COMPLETE, {
        report_alias: res.alias,
        dashboard_alias: props.dashboard_alias,
        report_type: 'dashboard',
      });

      wr.push(`/report/${res.alias}`);
      closeModal();
      addToast('Report created successfully', 'success');
    } catch (error) {
      console.error(error);
      addToast('Failed to create report', 'fail');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal.Layout modalSize="s">
      <Modal.Header>
        <Modal.Title>New Report</Modal.Title>
        <Modal.Desc>Capture a snapshot of data to preserve.</Modal.Desc>
      </Modal.Header>
      <Modal.Body>
        <VFlex g={8}>
          <Typo type="label3" weight="sb" className="text-gray-800">
            Title
          </Typo>
          <Input
            sizeType="l"
            placeholder="Enter title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </VFlex>
      </Modal.Body>
      <Modal.Footer
        cancel={{
          onClick: closeModal,
          disabled: isLoading,
        }}
        confirm={{
          onClick: onConfirm,
          isLoading,
          disabled: isDashboardLoading,
        }}
      />
    </Modal.Layout>
  );
};

export default CreateReportModal;
